import { addDays, addYears, format } from "date-fns";

/**
 * 企業共用配額（儲存空間 + 下載流量 + Email 總數量 + 企業 AI 用量）表單的預設值與驗證。
 *
 * 一組企業層級、全微服務共用的總額；帶生效期間與啟用開關，不區分試用。
 * 對應後端 GET/PUT /api/enterprise_management/{id}/usage。
 */

/**
 * 共用配額連動係數：以「該企業啟用中服務的席位上限總和」為基數換算。
 *  - 儲存空間(GB) = 總席位 × STORAGE_GB_PER_SEAT
 *  - 下載流量(GB) = 總席位 × TRAFFIC_GB_PER_SEAT
 * 後端亦有相同定義（EnterpriseUsage::STORAGE_GB_PER_SEAT / TRAFFIC_GB_PER_SEAT），
 * 後端存檔時為權威來源，本處僅供前端即時預覽。
 *
 * Email 總數量（email_quota）與企業 AI 用量（ai_quota_nt）刻意不在此列：
 * 兩者都是「每企業一份」的月配額，與席位數無關（定價規則以級距計費），
 * 因此沒有建議值、只由人工填寫。
 */
export const STORAGE_GB_PER_SEAT = 5;
export const TRAFFIC_GB_PER_SEAT = 1;

// 由「啟用中服務席位上限總和」換算共用配額。
export const computeUsageQuota = (totalActiveSeats = 0) => {
  const seats = Math.max(0, Number(totalActiveSeats) || 0);
  return {
    storage_gb: seats * STORAGE_GB_PER_SEAT,
    traffic_gb: seats * TRAFFIC_GB_PER_SEAT,
  };
};

// 首次尚未設定時的預設值：開始日=明天、結束日=明天+一年、容量/流量 0、未啟用。
export const computeUsageDefaults = (today = new Date()) => {
  const start = addDays(today, 1);
  const end = addYears(start, 1);
  return {
    start_date: format(start, "yyyy-MM-dd"),
    expiration_date: format(end, "yyyy-MM-dd"),
    storage_gb: 0,
    traffic_gb: 0,
    email_quota: 0,
    ai_quota_nt: 0,
    activate: false,
  };
};

const isNonNegativeInteger = (v) => Number.isInteger(v) && v >= 0;

/**
 * 儲存前驗證，回傳 { ok, errors }。errors 以欄位名為 key。
 *  - 結束日不可早於開始日
 *  - 容量/流量/Email 總數量/企業 AI 用量必須為非負整數（0 或正整數）
 *  - 啟用時：日期不可留空，且容量/流量必須大於 0
 *
 * Email 總數量與企業 AI 用量都不納入「啟用需大於 0」：這兩個欄位是後加的，既有已啟用
 * 企業的值一律是 0，若比照容量/流量要求大於 0，他們光是重存其他欄位就會被擋下。
 * 沒買企業 AI 帳戶的企業更是本來就該填 0（§5-2 A 僅企業「可」購買，不是必買）。
 * 同理，缺欄（undefined / null）視為 0 而非格式錯誤 —— 後端尚未部署這些欄位時
 * 讀回來的 usage 不會有它們，此時整份表單不該因此被判為不合法。
 */
export const validateUsage = (values = {}, { skipQuotaMin = false } = {}) => {
  const {
    start_date,
    expiration_date,
    storage_gb,
    traffic_gb,
    email_quota,
    ai_quota_nt,
    activate,
  } = values;
  const errors = {};

  if (!isNonNegativeInteger(storage_gb)) {
    errors.storage_gb = "儲存空間容量需為 0 或正整數";
  }
  if (!isNonNegativeInteger(traffic_gb)) {
    errors.traffic_gb = "下載流量需為 0 或正整數";
  }
  const emailQuotaValue =
    email_quota === undefined || email_quota === null ? 0 : email_quota;
  if (!isNonNegativeInteger(emailQuotaValue)) {
    errors.email_quota = "Email 總數量需為 0 或正整數";
  }
  const aiQuotaValue =
    ai_quota_nt === undefined || ai_quota_nt === null ? 0 : ai_quota_nt;
  if (!isNonNegativeInteger(aiQuotaValue)) {
    errors.ai_quota_nt = "企業 AI 用量需為 0 或正整數";
  }

  if (
    start_date &&
    expiration_date &&
    new Date(expiration_date) < new Date(start_date)
  ) {
    errors.expiration_date = "結束日期不可早於開始日期";
  }

  if (activate) {
    if (!start_date) errors.start_date = "請填寫開始日期";
    if (!expiration_date) errors.expiration_date = "請填寫結束日期";
    // 容量/流量由席位總和換算（唯讀）時，允許為 0，不做「啟用需大於 0」限制。
    if (!skipQuotaMin) {
      if (!errors.storage_gb && storage_gb < 1) {
        errors.storage_gb = "啟用時儲存空間容量需大於 0";
      }
      if (!errors.traffic_gb && traffic_gb < 1) {
        errors.traffic_gb = "啟用時下載流量需大於 0";
      }
    }
  }

  return { ok: Object.keys(errors).length === 0, errors };
};

// 表單是否有變動（與載入時的初始值相比）。用來決定「儲存變更」鈕是否出現。
const FIELDS = [
  "start_date",
  "expiration_date",
  "storage_gb",
  "traffic_gb",
  "email_quota",
  "ai_quota_nt",
  "activate",
];
export const isUsageDirty = (form = {}, initial = {}) =>
  FIELDS.some((k) => form[k] !== initial[k]);
